/**
 * La fiche d'un élève, vue par un de ses enseignants (docs/12 §6, RM-05 / RM-06).
 *
 * **La frontière entre les deux règles est déjà tracée dans les données que le
 * serveur envoie**, et cet écran ne la retrace pas : `courses` porte le plan
 * complet parce que ce sont les cours de l'appelant, `other_courses` ne porte
 * que titre, avancement et dernière activité parce que ce sont ceux des autres.
 * Il n'y a rien à masquer ici — les scores hors de ses cours ne sont pas dans la
 * réponse, et c'est ce qui rend cet écran incapable de les afficher.
 *
 * `consent_required` est affiché **explicitement** : sans lui, une section vide
 * ne distinguerait pas « il ne suit rien d'autre » de « il n'a pas consenti », et
 * l'enseignant ne saurait pas qu'il peut en faire la demande (docs/07 §6).
 */
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useStudentFile } from "@/features/learning/api/useDashboards";
import { Alert } from "@/shared/ui/Alert";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function StudentFilePage() {
  // `/teach/teach/…` : la route de layout est pathless, et son identifiant
  // préfixe celui de ses enfants sans ajouter de segment d'URL.
  const { studentId } = useParams({ from: "/teach/teach/students/$studentId" });
  const { t } = useTranslation("learning");
  const file = useStudentFile(studentId);

  if (file.isPending) {
    return <Skeleton className="mx-auto my-8 h-96 w-full max-w-4xl" label={t("student.title")} />;
  }
  if (file.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={file.error} />
      </div>
    );
  }

  const data = file.data;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{data.student_name}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("student.subtitle")}</p>
      </header>

      <Card title={t("student.myCourses")}>
        <ul className="space-y-4">
          {data.courses.map((course) => (
            <li key={course.course_id} className="space-y-2">
              <p className="font-medium">{course.course_title}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t("player.progress", {
                  done: course.completed_lesson_count,
                  total: course.lesson_count,
                  percent: course.progress_pct,
                })}
              </p>
              <ul className="space-y-1 text-sm">
                {course.lessons.map((lesson) => (
                  <li key={lesson.lesson_id} className="flex justify-between gap-2">
                    <span>{lesson.title}</span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t(`state.${lesson.state}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Card>

      <Card title={t("student.results")}>
        {data.recent_results.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("dashboard.recentEmpty")}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.recent_results.map((result) => (
              <li key={result.attempt_id} className="flex flex-wrap justify-between gap-2">
                <span>{result.exercise_title}</span>
                <span>
                  {result.score_pct === null
                    ? t("dashboard.noScore")
                    : t("attempt.percent", { value: result.score_pct })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("student.otherCourses")}>
        {data.consent_required ? (
          <Alert tone="info">{t("student.consentRequired")}</Alert>
        ) : data.other_courses.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("student.otherCoursesEmpty")}
          </p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.other_courses.map((course) => (
              <li key={course.course_id} className="flex flex-wrap justify-between gap-2">
                <span>{course.course_title}</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {t("attempt.percent", { value: course.progress_pct })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
